'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { MOBILE_NAV_ITEMS } from '@/src/lib/mobile-nav-config';
import { deriveContextFromPath, type NavigationContext } from '@/src/lib/navigation';
import { setActiveContextAction } from '@/src/server/actions/set-active-context.action';

export function MobileBottomNav({
  activeContext,
  availableContexts = MOBILE_NAV_ITEMS.map((item) => item.context),
}: {
  activeContext?: NavigationContext;
  availableContexts?: readonly NavigationContext[];
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const currentContext = activeContext ?? deriveContextFromPath(pathname ?? '/');
  const items = MOBILE_NAV_ITEMS.filter((item) => availableContexts.includes(item.context));

  return (
    <nav
      aria-label="Hauptnavigation"
      data-tour="mobile-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] pb-[env(safe-area-inset-bottom)] shadow-[var(--k-shadow-md)] lg:hidden"
    >
      {items.map((item) => {
        const active = currentContext === item.context;
        return (
          <Link
            key={item.id}
            href={item.href}
            aria-current={active ? 'page' : undefined}
            data-tour={`mobile-nav-${item.id}`}
            aria-label={item.label}
            aria-disabled={pending || undefined}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              if (pending) return;
              startTransition(async () => {
                const formData = new FormData();
                formData.set('context', item.context);
                const result = await setActiveContextAction(formData);
                if (result.ok) router.push(item.href);
              });
            }}
            className={`flex min-w-0 min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 px-1 pt-1.5 pb-1 text-[10px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] focus-visible:ring-inset ${
              active
                ? 'bg-[var(--k-color-accent-soft)] text-[var(--k-color-text-link)]'
                : 'text-[var(--k-color-text-secondary)] hover:bg-[var(--k-color-surface-muted)]'
            }`}
          >
            <span aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--k-color-surface-muted)] text-sm font-bold">
              {item.icon}
            </span>
            <span className="truncate">{item.shortLabel}</span>
          </Link>
        );
      })}
    </nav>
  );
}
